"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Button, ButtonVariantTypes } from "components/ui/button";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import Modal from "./components/Modal";
import useLastViewedPhoto from "./utils/useLastViewedPhoto";
interface ImageGridProps {
  bgColor: string;
  images: { alt: string; src: string }[];
  tags?: string[];
  width?: number;
  height?: number;
  wrapperClass?: string;
  title?: string;
  subtitle?: string;
  titleTag: React.ElementType;
  showAltLabel?: boolean;
}

const ImageGrid = ({
  bgColor = "#F2F0E6",
  title,
  subtitle,
  wrapperClass,
  tags = [],
  images: initialImages,
  titleTag = "p",
  showAltLabel = false
}: ImageGridProps) => {
  const TitleTag = titleTag;
  const [lastViewedPhoto, setLastViewedPhoto] = useLastViewedPhoto();
  const [photoId, setPhotoId] = useState<null | number>(null);
  const [showAll, setShowAll] = useState(false);

  const lastViewedPhotoRef = useRef<HTMLAnchorElement>(null);

  const images = initialImages.map((image, index) => ({
    ...image,
    id: index
  }));

  useEffect(() => {
    // This effect keeps track of the last viewed photo in the modal to keep the index page in sync when the user navigates back
    if (lastViewedPhoto && !photoId) {
      lastViewedPhotoRef.current?.scrollIntoView({ block: "center" });
      setLastViewedPhoto(null);
    }
  }, [lastViewedPhoto, setLastViewedPhoto, photoId]);

  const onChangePhotoId = (newVal: number) => {
    setPhotoId(newVal);
  };

  const showMoreImagesButton = images.length > 8 && !showAll;

  const finalImages = useMemo(() => {
    if (images.length > 8 && !showAll) {
      return images.slice(0, 8);
    }

    return images;
  }, [images, showAll]);

  return (
    <>
      {photoId !== null && (
        <Modal
          images={images}
          onClose={() => {
            setPhotoId(null);
            setLastViewedPhoto(lastViewedPhoto);
          }}
          photoId={photoId}
          onChangePhotoId={onChangePhotoId}
          showAltLabel={showAltLabel}
        />
      )}
      <section
        id="ImageGrid-4"
        style={{
          backgroundColor: bgColor
        }}
      >
        <MaxWidthWrapper className={wrapperClass}>
          <div className="w-full @container">
            <section id="image-grid" className="title-magical text-center font-matter">
              <div className="">
                <div className="tile-title-container ">
                  <Image src="/stars-title.svg" alt="stars" className="relative inline" width={66} height={66} />
                  <TitleTag className="tile-main-header font-heading [text-wrap:balance]">{title}</TitleTag>

                  {subtitle && <p className="tile-subtitle [text-wrap:balance]">{subtitle}</p>}
                </div>
                {tags.length > 0 && (
                  <div className="flex items-center justify-center gap-x-2 overflow-auto whitespace-nowrap pb-12 pt-8">
                    {tags.map(
                      (
                        tag: string,
                        index: number // <- Add types here
                      ) => (
                        <span key={index} className="bg-soft400 rounded-full px-4 py-2 text-sm text-primary-600">
                          {tag}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </section>
            <section id="imageGrid">
              <div className="">
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 @2xl:grid-cols-2 @2xl:gap-y-10 @5xl:grid-cols-3 @7xl:grid-cols-4 @7xl:gap-x-8">
                  {finalImages.map((image, index) => (
                    <div
                      key={index}
                      className="after:content after:shadow-highlight group relative flex aspect-square w-full cursor-pointer justify-center align-middle after:pointer-events-none after:absolute after:inset-0  after:rounded-lg"
                      onClick={() => setPhotoId(image.id)}
                    >
                      <Image
                        src={image.src}
                        alt={image.alt || "Home Renovation"}
                        fill
                        priority={true}
                        className="transform rounded-2xl object-cover brightness-90 transition will-change-auto group-hover:opacity-75"
                        style={{ transform: "translate3d(0, 0, 0)" }}
                        sizes="(max-width: 640px) 50vw,
                  (max-width: 1280px) 33vw,
                  (max-width: 1536px) 25vw,
                  25vw"
                      />
                      {!!(showAltLabel && image.alt) && (
                        <div
                          className={
                            "absolute bottom-1 right-1 max-w-[50%] select-none truncate rounded-full bg-neutral-dark-100 bg-opacity-60 px-2 py-1 text-xs font-medium text-white"
                          }
                        >
                          {image.alt}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mb-4 flex flex-wrap justify-center"></div>
                {showMoreImagesButton && (
                  <div className="mt-14 flex justify-center">
                    <Button variant={ButtonVariantTypes.OUTLINE} onClick={() => setShowAll(true)}>
                      Browse more inspiration
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </div>
        </MaxWidthWrapper>
      </section>
    </>
  );
};

export default ImageGrid;
